/**
 * OData entity and field names used by HttpD365Client (live mode).
 * Every name here is VERIFY until confirmed against the sandbox
 * (`GET /data/LegalEntities?$filter=LegalEntityId eq 'USMF'`, `$metadata`).
 * A field mapped to `null` is not sent until its name is known.
 * Mirrored in docs/VERIFY.md (V-W6).
 */

/**
 * CONFIRMED 2026-10-14 on a DEV box: POST /data/LegalEntities with LegalEntityId, Name,
 * AddressCountryRegionId, AddressStreet, AddressCity, AddressState, AddressZipCode,
 * AddressDescription and TimeZone ("GMTMINUS0500EASTERNTIME") returned 201. Search name is
 * NameAlias. Still VERIFY: PATCH of the address on an existing company (step 3).
 */
export const LEGAL_ENTITY = {
  entitySet: 'LegalEntities',
  key: 'LegalEntityId',
  fields: {
    id: 'LegalEntityId',
    name: 'Name',
    searchName: 'NameAlias' as string | null,
    timeZone: 'TimeZone',
    countryRegion: 'AddressCountryRegionId',
    street: 'AddressStreet',
    city: 'AddressCity',
    state: 'AddressState',
    zip: 'AddressZipCode',
    addressDescription: 'AddressDescription',
  },
  /** Country for every venue address (US venues only in v1). */
  defaultCountry: 'USA',
} as const;

/** CONFIRMED 2026-10-14 (DEV box, GET SystemUsers). `Enabled` is a real boolean. */
export const SYSTEM_USER = {
  entitySet: 'SystemUsers',
  fields: {
    id: 'UserID',
    name: 'UserName',
    upn: 'Alias',
    email: 'Email',
    networkDomain: 'NetworkDomain',
    company: 'Company',
    enabled: 'Enabled',
    accountType: 'AccountType',
    // CONFIRMED 2026-10-15 (POST returned 201): both are required on create, and the names are
    // case-sensitive ("Language" and "HelpLanguage" are not these fields).
    language: 'UserInfo_language',
    helpLanguage: 'Helplanguage',
  },
  networkDomain: 'https://sts.windows.net/',
  accountType: 'ClaimsUser',
  /** Language and help language for every user the app creates. */
  language: 'en-us',
} as const;

/** CONFIRMED 2026-10-14 (GET SecurityUserRoles). */
export const SECURITY_USER_ROLE = {
  entitySet: 'SecurityUserRoles',
  fields: {
    userId: 'UserId',
    roleId: 'SecurityRoleIdentifier',
    // Required on POST: D365 checks the role by identifier and name (CONFIRMED 2026-10-15).
    roleName: 'SecurityRoleName',
    status: 'AssignmentStatus',
    mode: 'AssignmentMode',
  },
  enabled: 'Enabled',
  manual: 'Manual',
} as const;

/**
 * The role D365 gives every new user by itself, for all companies (seen 2026-10-23 on users the app
 * created). The app limits it to the venue company on the users it creates. VERIFY: the identifier,
 * assumed upper case like the confirmed roles in ASSIGNABLE_D365_ROLES.
 */
export const DEFAULT_USER_ROLE = 'SYSTEMUSER';

/**
 * CONFIRMED 2026-10-14 (GET SecurityUserRoleOrganizations). Scopes a role
 * assignment to one company; without a row here the role applies to all companies.
 */
export const SECURITY_USER_ROLE_ORG = {
  entitySet: 'SecurityUserRoleOrganizations',
  fields: {
    userId: 'UserId',
    roleId: 'SecurityRoleIdentifier',
    // No SecurityRoleName on this entity (D365 rejected it 2026-10-15).
    orgType: 'OrganizationType',
    orgId: 'OrganizationId',
    operatingUnitType: 'OperatingUnitType',
  },
  legalEntity: 'LegalEntity',
  noOperatingUnit: 'None',
} as const;

/** Security roles, for looking up identifiers (`SecurityRoleIdentifier`, `SecurityRoleName`). */
export const SECURITY_ROLE = {
  entitySet: 'SecurityRoles',
  fields: { id: 'SecurityRoleIdentifier', name: 'SecurityRoleName' },
} as const;

/**
 * D365 security roles the app may assign (SecurityRoleIdentifier → name), CONFIRMED
 * 2026-10-14 against `GET /data/SecurityRoles` on the DEV box. The people file's Roles
 * sheet uses these identifiers directly (template role catalog, shared/catalog.ts).
 * The allow list: the live slice assigns no other role (never an admin role).
 */
export const ASSIGNABLE_D365_ROLES: Record<string, string> = {
  LEDGERACCOUNTINGMANAGER: 'Accounting manager',
  // VERIFY: identifier of the Accountant role (the GL accountant's role, added 2026-10-23).
  LEDGERACCOUNTANT: 'Accountant',
  VENDPURCHASINGAGENT: 'Purchasing agent',
  VENDINVOICEACCOUNTSPAYABLECLERK: 'Accounts payable clerk',
  VENDINVOICEACCOUNTSPAYABLEMANAGER: 'Accounts payable manager',
  PROJPROJECTMANAGER: 'Project manager',
  LEDGERFINANCIALCONTROLLER: 'Financial controller',
};

/**
 * Work calendar (Calendar entity, WorkCalendarEntity). Field names follow the staging columns
 * CONFIRMED in the export (CALENDARID, CALENDARNAME, WORKHOURS, DEFAULTSTARTINGTIME, IS…WORKINGDAY).
 * Entity set and field names CONFIRMED 2026-10-23 against a USMF record (times are integers).
 * VERIFY: that the default times are seconds after midnight (24:00 = 86400).
 */
export const WORK_CALENDAR = {
  entitySet: 'WorkCalendars',
  fields: {
    id: 'CalendarId',
    name: 'CalendarName',
    workHours: 'WorkHours',
    start: 'DefaultStartingTime',
    end: 'DefaultEndingTime',
    /** Monday to Sunday */
    days: [
      'IsMondayWorkingDay',
      'IsTuesdayWorkingDay',
      'IsWednesdayWorkingDay',
      'IsThursdayWorkingDay',
      'IsFridayWorkingDay',
      'IsSaturdayWorkingDay',
      'IsSundayWorkingDay',
    ],
  },
  yes: 'Yes',
  no: 'No',
} as const;

// ── People (Import people, live) ─────────────────────────────────────────────
// Entity sets and field names CONFIRMED 2026-10-23 against USMF records in the sales-cd9 sandbox.
// VERIFY: that positions and workers take the app's IDs (RSM2-GLACCT, RSM2001), i.e. their number
// sequences allow manual numbers.

/** Open-ended valid-to, as D365 writes it */
export const OPEN_END = '2154-12-31T23:59:59Z';

export const JOB = { entitySet: 'Jobs', fields: { id: 'JobId', description: 'Description' } } as const;

export const WORKER = {
  entitySet: 'Workers',
  fields: {
    personnelNumber: 'PersonnelNumber',
    firstName: 'FirstName',
    lastName: 'LastName',
    email: 'PrimaryContactEmail',
    partyNumber: 'PartyNumber',
  },
  /** Set on the employment; the worker's WorkerType is derived from it and rejects inserts */
  employee: 'Employee',
} as const;

export const EMPLOYMENT = {
  entitySet: 'Employments',
  fields: {
    personnelNumber: 'PersonnelNumber',
    company: 'LegalEntityId',
    start: 'EmploymentStartDate',
    end: 'EmploymentEndDate',
    type: 'WorkerType',
  },
} as const;

export const POSITION = {
  entitySet: 'PositionsV2',
  fields: {
    id: 'PositionId',
    description: 'Description',
    jobId: 'JobId',
    department: 'DepartmentNumber',
    paidBy: 'PaidByLegalEntity',
    activation: 'Activation',
    retirement: 'Retirement',
    // VERIFY: field name. Required on create ("Field 'Pay cycle' must be filled in", 2026-10-23).
    payCycle: 'PayCycleId',
  },
  /** Pay cycle for every position the app creates (monthly, per the user 2026-10-23) */
  payCycle: 'M',
} as const;

export const POSITION_HIERARCHY = {
  entitySet: 'PositionHierarchies',
  fields: {
    id: 'PositionId',
    parent: 'ParentPositionId',
    type: 'HierarchyTypeName',
    from: 'ValidFrom',
    to: 'ValidTo',
  },
  /** The managerial hierarchy workflows route by */
  line: 'Line',
} as const;

export const POSITION_ASSIGNMENT = {
  entitySet: 'PositionWorkerAssignmentsV2',
  fields: {
    positionId: 'PositionId',
    personnelNumber: 'PersonnelNumber',
    primary: 'IsPrimaryPosition',
    from: 'ValidFrom',
    to: 'ValidTo',
  },
  yes: 'Yes',
  no: 'No',
} as const;

/**
 * Pay cycles (positions need one: "Field 'Pay cycle' must be filled in"). The Legends TRN sandbox
 * has none (2026-10-27), so the app creates M. VERIFY entity set and fields (PayCycles answered a
 * GET in TRN; PayCycleFrequency and its value "Monthly" are assumed).
 */
export const PAY_CYCLE = {
  entitySet: 'PayCycles',
  fields: { id: 'PayCycleId', description: 'Description', frequency: 'PayCycleFrequency' },
  monthly: 'Monthly',
} as const;

export const PERSON_USER = {
  entitySet: 'PersonUsers',
  fields: { userId: 'UserId', partyNumber: 'PartyNumber', from: 'ValidFrom', to: 'ValidTo' },
} as const;

// ── Master data (customers, vendors, project contracts, projects tabs) ────────
// Everything below is VERIFY against the target sandbox's $metadata (V-W7 in docs/VERIFY.md).
// Company-specific entities take `dataAreaId` on create; reads filter on it with cross-company=true.

export const DATA_AREA_FIELD = 'dataAreaId';

/**
 * Field names CONFIRMED 2026-10-22 against a customer record from the RSM demo sandbox (USMF).
 * VERIFY: the entity set name CustomersV3, and a create in a venue company.
 */
export const CUSTOMER = {
  entitySet: 'CustomersV3',
  fields: {
    account: 'CustomerAccount',
    name: 'OrganizationName',
    group: 'CustomerGroupId',
    currency: 'SalesCurrencyCode',
    partyType: 'PartyType',
    phone: 'PrimaryContactPhone' as string | null,
    email: 'PrimaryContactEmail' as string | null,
    street: 'AddressStreet',
    city: 'AddressCity',
    state: 'AddressState',
    zip: 'AddressZipCode',
    country: 'AddressCountryRegionId',
    paymentTerms: 'PaymentTerms' as string | null,
  },
  organization: 'Organization',
} as const;

/**
 * Field names CONFIRMED 2026-10-22 against a vendor record from the RSM demo sandbox (USMF);
 * payment terms and method are the Default… fields. VERIFY: a create in a venue company.
 */
export const VENDOR = {
  entitySet: 'VendorsV2',
  fields: {
    account: 'VendorAccountNumber',
    name: 'VendorOrganizationName',
    group: 'VendorGroupId',
    currency: 'CurrencyCode',
    partyType: 'VendorPartyType',
    phone: 'PrimaryPhoneNumber' as string | null,
    email: 'PrimaryEmailAddress' as string | null,
    street: 'AddressStreet',
    city: 'AddressCity',
    state: 'AddressStateId',
    zip: 'AddressZipCode',
    country: 'AddressCountryRegionId',
    paymentTerms: 'DefaultPaymentTermsName' as string | null,
    paymentMethod: 'DefaultVendorPaymentMethodName' as string | null,
  },
  organization: 'Organization',
} as const;

/** VERIFY: group entities and their key fields. */
export const MASTER_DATA_GROUPS = {
  customer: { entitySet: 'CustomerGroups', key: 'CustomerGroupId' },
  vendor: { entitySet: 'VendorGroups', key: 'VendorGroupId' },
  project: { entitySet: 'ProjectGroups', key: 'ProjectGroupId' },
} as const;

/**
 * Project contract header and its customer funding source. Field names CONFIRMED 2026-10-22
 * against sandbox records: the key is ProjectContractID on both, the customer is
 * CustomerOrOrganization, and CreateDefaultFundingRule = Yes gives the contract its 100 %
 * funding rule. VERIFY: the entity set names.
 */
export const PROJECT_CONTRACT = {
  entitySet: 'ProjectContracts',
  fields: { id: 'ProjectContractID', name: 'Name' },
} as const;
export const PROJECT_FUNDING_SOURCE = {
  entitySet: 'ProjectFundingSources',
  fields: {
    contractId: 'ProjectContractID',
    sourceId: 'FundingSourceId',
    type: 'FundingType',
    customer: 'CustomerOrOrganization',
    defaultRule: 'CreateDefaultFundingRule',
  },
  customerType: 'Customer',
  yes: 'Yes',
} as const;

/**
 * Field names CONFIRMED 2026-10-22 against a project record from the sandbox (manager and
 * controller are worker personnel numbers; dates are DateTimeOffset). VERIFY: the entity set name.
 */
export const PROJECT = {
  entitySet: 'Projects',
  fields: {
    id: 'ProjectID',
    name: 'ProjectName',
    group: 'ProjectGroup',
    type: 'ProjectType',
    contractId: 'ProjectContractID',
    parentId: 'ParentProject' as string | null,
    manager: 'WorkerResponsiblePersonnelNumber' as string | null,
    controller: 'WorkerRespFinancialPersonnelNumber' as string | null,
    startDate: 'ProjectedStartDate' as string | null,
    endDate: 'ProjectedEndDate' as string | null,
  },
} as const;

/** Projects sheet type → D365 ProjectType enum value (TimeMaterial CONFIRMED 2026-10-22; VERIFY the others). */
export const PROJECT_TYPE_ENUM = {
  Internal: 'Internal',
  Cost: 'Cost',
  TimeAndMaterial: 'TimeMaterial',
  FixedPrice: 'FixedPrice',
} as const;

/**
 * IANA time zone (intake answer) → D365 TimeZone enum value. Eastern (2026-10-14) and Central
 * (2026-10-15) are CONFIRMED; VERIFY the others against the values D365 returns.
 */
export const TIME_ZONE_ENUM: Record<string, string> = {
  'America/New_York': 'GMTMINUS0500EASTERNTIME',
  'America/Chicago': 'GMTMINUS0600CENTRALTIME',
  'America/Denver': 'GMTMINUS0700MOUNTAINTIME',
  'America/Phoenix': 'GMTMINUS0700ARIZONA',
  'America/Los_Angeles': 'GMTMINUS0800PACIFICTIME',
  'America/Anchorage': 'GMTMINUS0900ALASKA',
  'Pacific/Honolulu': 'GMTMINUS1000HAWAII',
};
